import { useEffect } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, BadgePercent, RefreshCcw, Send, X } from "lucide-react";
import referralHero from "../assets/referrals/referral-hero.png";

interface ReferralInviteModalProps {
  open: boolean;
  isSaving?: boolean;
  onDismiss: () => void;
  onInvite: () => void;
}

export default function ReferralInviteModal({ open, isSaving = false, onDismiss, onInvite }: ReferralInviteModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSaving) onDismiss();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isSaving, onDismiss, open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/65 p-3 backdrop-blur-sm sm:px-4 sm:py-6"
      role="dialog"
      aria-modal="true"
      aria-label="Give 50%. Get 50%."
      onPointerDown={(event) => {
        if (event.target === event.currentTarget && !isSaving) onDismiss();
      }}
    >
      <div
        className="relative max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl overflow-y-auto rounded-3xl border border-white/70 bg-[#fffdfb] shadow-2xl dark:border-dark-700 dark:bg-dark-900 sm:max-h-[calc(100vh-3rem)] sm:rounded-[2rem]"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          disabled={isSaving}
          aria-label="Dismiss referral invitation"
          className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition hover:bg-white hover:text-gray-800 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-40 dark:hover:bg-dark-700 dark:hover:text-white sm:right-5 sm:top-5 sm:h-10 sm:w-10"
        >
          <X size={20} strokeWidth={2.2} />
        </button>

        <div className="grid grid-cols-[0.94fr_1.06fr] items-center gap-1 px-5 pb-4 pt-7 sm:grid-cols-[0.92fr_1.08fr] sm:gap-3 sm:px-10 sm:pb-2 sm:pt-11">
          <div className="relative z-[1]">
            <p className="text-[9px] font-black uppercase tracking-[0.18em] text-violet-700 dark:text-violet-300 sm:text-xs sm:tracking-[0.22em]">Refer a friend</p>
            <h2 id="referral-invite-modal-title" className="mt-2 text-[1.8rem] font-black leading-[0.95] tracking-[-0.05em] text-gray-950 dark:text-white sm:mt-3 sm:text-[3.15rem] sm:leading-[0.98] sm:tracking-[-0.045em]">
              Half price<br />for them.<br />Half price<br />for you.
            </h2>
            <span className="mt-2 block h-1 w-14 rounded-full bg-[#ff7968] sm:mt-3 sm:h-1.5 sm:w-24" aria-hidden="true" />
          </div>

          <div className="relative flex min-h-0 items-center justify-center sm:-mb-2 sm:-ml-10 sm:min-h-72 sm:items-end">
            <div className="absolute inset-x-1 bottom-0 top-0 rounded-[44%] bg-violet-50 dark:bg-violet-500/10 sm:inset-x-6 sm:bottom-3 sm:top-2" aria-hidden="true" />
            <img
              src={referralHero}
              alt="Two friends sharing a DealHawk referral reward"
              className="relative z-[1] w-full max-w-[185px] object-contain drop-shadow-[0_10px_14px_rgba(76,29,149,0.10)] sm:max-w-[390px] sm:drop-shadow-[0_14px_20px_rgba(76,29,149,0.10)]"
            />
          </div>
        </div>

        <div className="border-y border-gray-100 bg-white px-4 py-4 dark:border-dark-700 dark:bg-dark-800/40 sm:px-10 sm:py-6">
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div className="block text-center">
              <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-violet-600 text-xs font-black text-violet-700 dark:border-violet-400 dark:text-violet-300 sm:h-11 sm:w-11 sm:text-sm">1</div>
              <Send className="hidden text-gray-950 dark:text-white sm:mx-auto sm:mt-4 sm:block" size={29} strokeWidth={2} />
              <p className="mt-2 text-[10px] font-extrabold leading-[1.25] text-gray-950 dark:text-white sm:text-sm sm:leading-5">Share your invite</p>
            </div>
            <div className="block text-center">
              <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-violet-600 text-xs font-black text-violet-700 dark:border-violet-400 dark:text-violet-300 sm:h-11 sm:w-11 sm:text-sm">2</div>
              <BadgePercent className="hidden text-[#ff7968] sm:mx-auto sm:mt-4 sm:block" size={30} strokeWidth={2} />
              <p className="mt-2 text-[10px] font-extrabold leading-[1.25] text-gray-950 dark:text-white sm:text-sm sm:leading-5">They get 50% off<br className="hidden sm:block" /> their first paid month</p>
            </div>
            <div className="block text-center">
              <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-violet-600 text-xs font-black text-violet-700 dark:border-violet-400 dark:text-violet-300 sm:h-11 sm:w-11 sm:text-sm">3</div>
              <RefreshCcw className="hidden text-gray-950 dark:text-white sm:mx-auto sm:mt-4 sm:block" size={29} strokeWidth={2} />
              <p className="mt-2 text-[10px] font-extrabold leading-[1.25] text-gray-950 dark:text-white sm:text-sm sm:leading-5">You get 50% off<br className="hidden sm:block" /> one renewal</p>
            </div>
          </div>
        </div>

        <div className="px-5 pb-4 pt-4 sm:px-10 sm:pb-8 sm:pt-5">
          <p className="text-center text-[11px] font-bold text-gray-500 dark:text-dark-200 sm:text-xs">
            Earn up to <span className="text-[#ff7968]">12 rewards</span> per year
          </p>
          <button
            type="button"
            onClick={onInvite}
            disabled={isSaving}
            className="mt-3 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-violet-700 px-5 text-sm font-black text-white shadow-lg shadow-violet-700/20 transition hover:-translate-y-0.5 hover:bg-violet-800 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-60 dark:bg-violet-600 dark:hover:bg-violet-500 dark:focus:ring-offset-dark-900 sm:mt-4 sm:h-13"
          >
            {isSaving ? "Saving…" : "Invite a friend"}
            {!isSaving && <ArrowRight size={18} />}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            disabled={isSaving}
            className="mt-1 h-8 w-full rounded-xl text-xs font-bold text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 disabled:opacity-40 dark:text-dark-300 dark:hover:bg-dark-800 dark:hover:text-white sm:mt-2 sm:h-10 sm:text-sm"
          >
            No thanks
          </button>
          <p className="mt-1 hidden text-center text-[11px] text-gray-400 dark:text-dark-400 sm:block">This invitation will not appear again after you close it.</p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
